import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteAttendance } from "@/hooks/api/useAttendances"
import type { DeleteAttendanceProps } from "@/types/attendance"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteAttendance = memo(
  ({ attendance, onSuccess, onCancel }: DeleteAttendanceProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteAttendanceMutation = useDeleteAttendance()

    const displayLabel = attendance.enrollment_id ?? attendance.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteAttendanceMutation.mutateAsync(attendance.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting attendance:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete attendance: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete Attendance"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This attendance will be
                permanently removed.
              </Text>
            </>
          }
          confirmText="Delete Attendance"
          confirmColorScheme="red"
          isLoading={deleteAttendanceMutation.isPending}
        />
      </>
    )
  },
)

DeleteAttendance.displayName = "DeleteAttendance"

export default DeleteAttendance
export type { DeleteAttendanceProps }
